<template>
  <div 
    v-if="!loading"
    v-mask="{ open }"
    class="glaxi-menu__search__container"> 
    <div class="glaxi-menu__search">
      <div class="glaxi-menu__search__form">
        <input 
          v-if="open"
          v-player="{ open, lang }"
          v-model="keywords"
          v-on:keyup="startSearch"
          type="search">
        <p v-if="keywords.length">
          {{ $tc('labelMenu.searchResults', searchResult.length) }}
        </p>
      </div>
      <div class="glaxi-menu__search__content">
        <ul v-if="searchResult.length">
          <li 
            v-for="(product, index) of searchResult"
            :key="index"
            class="glaxi-menu__search__content__li">
            <router-link :to="{ name: `${lang}.single-product`, params: { slug: product.fields.slug } }">
              <img
                :src="product.fields.productThumbnail.fields.file.url"
                :alt="product.fields.productThumbnail.fields.description"
                :width="product.fields.productThumbnail.fields.file.details.image.width"
                :height="product.fields.productThumbnail.fields.file.details.image.height"
                class="glaxi-menu__search__content__li__img">
              <p v-html="product.fields.title"/>
              <span v-if="typeof product.fields.productCode !== 'undefined'" v-html="product.fields.productCode" />
              <SingleProductListHasTags :product="product" />
            </router-link>
          </li>
        </ul>
      </div>  
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { TweenLite } from 'gsap/TweenLite'
import { TweenMax } from 'gsap/TweenMax'

import Typed from 'typed.js'
import Levenshtein from 'fast-levenshtein'
import messages from '../../../i18n/messages.js'

import SingleProductListHasTags from '@/components/SingleProductListHasTags/SingleProductListHasTags.vue'

// typed options
// https://mattboldt.com/typed.js/docs/

export default {
  name: 'MenuSearch',
  components: {
    SingleProductListHasTags
  },
  data: () => ({
    loading: true,
    searchResult: [],
    keywords: ''
  }),
  props: {
    open: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapGetters('contentful', [
      'products'
    ]),
    ...mapGetters([
      'lang',
      'touch'
    ]),
    productsMeta: {
      get () {
        return this.products.map(product => ({
          fields: product.fields,
          keywords: this.$_getKeywordsArray(product.fields.title + ' ' + product.fields.productCode)
        }))
      }
    }
  },
  created () {
    this.loadData()
  },
  methods: {
    ...mapActions('contentful', [
      'loadProducts'
    ]),
    startSearch (event) {
      if ((this.touch && event.key === 'Enter') || !this.touch){
        const userArray = this.$_getKeywordsArray(this.keywords)
        
        // check if enough data
        if (userArray.length <= 0) this.searchResult = []
        const bestMatches = this.productsMeta.reduce((bests, product) => {
          const { keywords } = product
          // [carre taglio]
          const scoreSum = keywords.reduce((score, keyword) => {
            var lowestScore = userArray
              .map(uKeyword => Levenshtein.get(uKeyword, keyword))
              .sort((a, b) => a - b)
              [0]
            if (lowestScore === 2) lowestScore -= 20
            else if (lowestScore === 1) lowestScore -= 40
            else if (lowestScore === 0) lowestScore -= 60
            return score + lowestScore
          }, 0)
          var avg = scoreSum / keywords.length

          // very similar match
          userArray.forEach(function(uWord) {
            const fieldTitle = product.fields.title.toLowerCase()
            if (fieldTitle.includes(uWord)) avg -= 25
          })
          
          if (avg < 2) bests.push(Object.assign({}, product, { score: avg }))
          return bests
        }, [])
        // sort and return 20 prods
        this.searchResult = bestMatches.sort((a, b) => {
            if (a.score < b.score) return -1
            if (a.score > b.score) return 1
            return 0
        }).slice(0, 20)
        if (this.touch) event.target.blur()
      }
    },
    $_getKeywordsArray (keywords){
      // array of words with more than 3 letters
      return keywords
        .split(" ")
        .filter((element) => element.length > 3)
        // .filter((element) => /^[A-Za-zÀ-ÖØ-öø-ÿ]+$/.test(element))
        .map((element) => element.toLowerCase())
    },
    $_getDistance (titleArray, keywordsArray){
      if (!keywordsArray.length) return 0
      const keyword = keywordsArray.pop()
      var distance = 0
      titleArray.forEach((titleword) => {
        distance += Levenshtein.get(titleword.substring(0, keyword.length), keyword)
        // console.log('recursion on ' + titleword + ' with ' + keyword)
        // TOLGO LA PAROLA
        // if (distance === 0)
      })
      return distance + this.$_getDistance (titleArray, keywordsArray)
    },
    async loadData () {
      this.loading = true
      try {
        await this.loadProducts()
        this.loading = false
      } catch (e) {
        // TODO sentry
      }
    }
  },
  directives: {
    'mask': {
      inserted (el, binding) {
        TweenLite.to(el, 0, {width:'0%'})
      },
      componentUpdated (el, binding) {
        const { open } = binding.value
        if (open) {
          TweenLite.to(el, .8, {ease: Power2. easeOut, width:'100%', onComplete: () => document.body.classList.add('--overflow-hidden')})
        }
        else{
          document.body.classList.remove('--overflow-hidden')
          TweenLite.to(el, .8, {ease: Power2. easeOut, width:'0%'})
        }
      }
    },
    'player': {
      inserted (el, binding) {
        const { lang } = binding.value
        var typed = new Typed(el, {
          strings: [messages[lang].labelMenu.searchExample01, messages[lang].labelMenu.searchExample02, messages[lang].labelMenu.searchExample03],
          contentType: 'html',
          typeSpeed: 100,
          backSpeed: 40,
          smartBackspace: true,
          startDelay: 1000,
          showCursor: {
            showCursor: true,
            cursorChar: '|',
            autoInsertCss: false
          },
          attr: 'placeholder'
        })
        el.focus()
      },
      componentUpdated (el, binding) {
        const { open } = binding.value
        // el.focus()
        // if (open) el.play()
        // else el.stop()
      }
    }
  }
}
</script>

<style lang="scss">
  @import "./search.scss";
  @import "./maskEffect.scss";
</style>
